import React, {
  // useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

import { useTranslation } from "react-i18next";
import FocusLock from "react-focus-lock";
import parse from "html-react-parser";
import i18next from "i18next";

import "./index.css";

import { SingleSelectQuestion } from "@labxchange/ui-components/lib/SingleSelectQuestion";
import { MultiSelectQuestion } from "@labxchange/ui-components/lib/MultiSelectQuestion";
import { default as i18n } from "@learningblocks/shared-components/lib/i18n";
import RQ from "./widgets/Rhetorical-Question-Widget";
import Video from "./widgets/Video-Widget";
import Animation from "./widgets/Animation-Widget";
import TabComp from "./widgets/Tab-Comparison-Widget";
import Slider from "./widgets/Slider-Widget";
import DataWidget from "./widgets/Data-Widget";
import NEQ from "./widgets/NEQ";
import {
  PCR2,
  Histogram1,
  Histogram2,
  Histogram3,
  Histogram4,
} from "@learningblocks/interactive-infographics";

import ActiveArrow from "./img/widgetLock-arrow-active.svg";
import InactiveArrow from "./img/widgetLock-arrow-inactive.svg";

export default function WidgetContainer(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const lang = "en";

  // Set focus to the content when the popup opens
  const contentRef = useRef(null);

  useEffect(() => {
    props.setScrollActive(false);
    const elmnt = document.getElementById("widget-screen");
    elmnt.scrollTop = 0;

    // Set focus to the content when the popup opens
    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.focus();
      }, 100);
    }
  }, [props.descriptionID]);

  // Handle escape key to close the popup if allowed
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && props.descriptionID !== 1) {
        forceAnimationUpdate("click", "up");
        props.forceScroll("nav");
      }
    },
    [props.descriptionID]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const forceAnimationUpdate = (e, direction) => {
    if (e === "click" || (e.key && e.key === "Enter")) {
      // Detected interaction (by click or enter with keyboard)

      if (direction === "up" && props.descriptionID !== "start") {
        console.log(
          "Processing UP navigation from descriptionID:",
          props.descriptionID
        );

        if (props.descriptionID !== 1) {
          // If not the first, go to previous
          if (
            props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
              .type === "widget"
          ) {
            // If navigating to previous from a widget, go to the end of the previous description
            const percentage = getPercentage(
              props.sceneData.Scrolly.descriptions[props.descriptionID - 2]
                .range.max,
              -1
            );

            console.log(
              "Navigating from widget to previous description end at percentage:",
              percentage
            );
            props.forceAnimationTo(percentage, -1); // second parameter is the number of frames to adjust for (considering matching start/end times)
          } else {
            props.forceAnimationTo(
              getPercentage(
                props.sceneData.Scrolly.descriptions[props.descriptionID - 2]
                  .range.min
              )
            );
          }
        } else {
          // Back to start
          window.scrollTo(0, 0);
          props.setDescriptionID("start");
        }
      }

      if (direction === "down" && props.descriptionID !== "end") {
        if (
          props.descriptionID === props.sceneData.Scrolly.descriptions.length
        ) {
          // If on the last, go to "end"
          console.log("On last description, going to end screen");
          props.setScrollActive(true);
          window.scrollTo(0, document.body.scrollHeight);
          props.setDescriptionID("end");
        } else {
          // Go to next
          props.setScrollActive(true);
          props.forceAnimationTo(
            getPercentage(
              props.sceneData.Scrolly.descriptions[props.descriptionID].range
                .min
            )
          );
        }
      }
    }
  };

  const getPercentage = (timeStamp, offset) => {
    let seconds = toSeconds(timeStamp);
    if (offset !== undefined) {
      seconds = seconds - 0.25;
    } // To ensure widget does not navigate to itself due to matching end and start times to previouse description
    const percentage = (seconds / props.totalTime) * 100;
    return percentage;
  };

  const toSeconds = (timeStamp) => {
    // "00:00.00" (minutes:seconds) to seconds for percentage calculations and comparisons
    const timeArr = timeStamp.split(":");
    const seconds = Number(timeArr[0]) * 60 + Number(timeArr[1]);
    return seconds;
  };

  const widgetData =
    props.sceneData.Scrolly.descriptions[props.descriptionID - 1].props;

  return (
    <FocusLock returnFocus>
      <div
        id="widget-screen"
        className="widget-screen"
        role="dialog"
        aria-modal="true"
        aria-describedby="widget-content"
      >
        <div
          className="widget-container"
          id="widget-container"
          aria-hidden="false"
        >
          <div
            id="widget-content"
            tabIndex="-1"
            ref={contentRef}
            className="sr-only visually-hidden"
            aria-live="assertive"
          >
            Interactive content popup
          </div>
          <div
            className="widget-prev-btn-container"
            style={{
              display:
                props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                  .props.type === "PCR2"
                  ? "none"
                  : null,
            }}
            id="widget-prev-btn-container"
          >
            <button
              className="widget-nav-button"
              aria-label={t("previous.string")}
              onClick={() => {
                forceAnimationUpdate("click", "up");
                props.forceScroll("nav");
              }} // Detect Click and pass button direction, also force animation scroll position to align
            >
              <img
                src={InactiveArrow}
                alt=""
                role="presentation"
                className="widget-prev-btn"
              />
            </button>
          </div>
          {!props.debug || !props.preview ? null : (
            <div
              className="widget-debug"
              role="button"
              tabIndex={0}
              onClick={() => {
                window.open(
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .recordURL,
                  "_blank"
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  window.open(
                    props.sceneData.Scrolly.descriptions[
                      props.descriptionID - 1
                    ].recordURL,
                    "_blank"
                  );
                }
              }}
            >
              {props.sceneData.Scrolly.descriptions[props.descriptionID - 1].id}
            </div>
          )}
          <div
            className="widget-content-container"
            style={{
              maxWidth: [
                "Histogram1",
                "Histogram2",
                "Histogram3",
                "Histogram4",
              ].includes(
                props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                  .props.type
              )
                ? "700px"
                : null,
              flexDirection: [
                "Histogram1",
                "Histogram2",
                "Histogram3",
                "Histogram4",
              ].includes(
                props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                  .props.type
              )
                ? "column"
                : null,
              margin: [
                "Histogram1",
                "Histogram2",
                "Histogram3",
                "Histogram4",
              ].includes(
                props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                  .props.type
              )
                ? "auto"
                : null,
            }}
            aria-live="polite"
          >
            {props.sceneData.Scrolly.descriptions[props.descriptionID - 1].props
              .type === "Single Select Question" ? (
              <SingleSelectQuestion
                key={props.descriptionID}
                content={[
                  "question",
                  ...(widgetData.image ? ["image"] : []),
                  "options",
                  ...(widgetData.hint ? ["hintDetails"] : []),
                ]}
                question={t(`${widgetData.question}.string`)}
                options={widgetData.options.map((opt) => ({
                  ...opt,
                  text: t(`${opt.text}.string`),
                  ...(opt.feedback && {
                    feedback: t(`${opt.feedback}.string`),
                  }),
                }))}
                answerId={
                  Array.isArray(widgetData.answerId)
                    ? widgetData.answerId
                    : [widgetData.answerId]
                }
                totalAttempts={
                  widgetData.options ? widgetData.options.length : undefined
                }
                {...(widgetData.image &&
                (widgetData.image.url || widgetData.imageURL)
                  ? {
                      image: {
                        src: props.preview
                          ? widgetData.imageURL
                          : `${window.CONTENT_BASE_URL}locales/${i18n.language}/images/${widgetData.image}`,
                        altText: widgetData.imageAlt
                          ? t(`${widgetData.imageAlt}.string`)
                          : "Question illustration",
                        imageId: widgetData.image,
                        containerBgColor: "#003e6b",
                        ...(widgetData.image.caption && {
                          caption: t(`${widgetData.image.caption}.string`),
                        }),
                      },
                    }
                  : {})}
                {...(widgetData.hint
                  ? {
                      hintDetails: {
                        label: t("Hint.string"),
                        open: false,
                        content: t(`${widgetData.hint}.string`),
                      },
                    }
                  : {})}
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Rhetorical Question" ? (
              <RQ
                key={props.descriptionID}
                preview={props.preview}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "MSCQ" ? (
              <MultiSelectQuestion
                key={props.descriptionID}
                content={[
                  "question",
                  ...(widgetData.image ? ["image"] : []),
                  "options",
                  ...(widgetData.hint ? ["hintDetails"] : []),
                ]}
                question={t(`${widgetData.question}.string`)}
                options={(
                  widgetData.options ||
                  widgetData.checkboxes ||
                  []
                ).map((opt) => ({
                  ...opt,
                  text: t(`${opt.text}.string`),
                  ...(opt.feedback && {
                    feedback: t(`${opt.feedback}.string`),
                  }),
                }))}
                answerId={
                  Array.isArray(widgetData.answerId || widgetData.answerIds)
                    ? widgetData.answerId || widgetData.answerIds
                    : [widgetData.answerId || widgetData.answerIds]
                }
                totalAttempts={
                  widgetData.retro
                    ? (widgetData.options || widgetData.checkboxes || []).length
                    : -1
                }
                {...(widgetData.image &&
                (widgetData.image.url || widgetData.imageURL)
                  ? {
                      image: {
                        src: props.preview
                          ? widgetData.imageURL
                          : `${window.CONTENT_BASE_URL}locales/${i18n.language}/images/${widgetData.image}`,
                        altText: widgetData.imageAlt
                          ? t(`${widgetData.imageAlt}.string`)
                          : "Question illustration",
                        containerBgColor: "#003e6b",
                        ...(widgetData.image.caption && {
                          caption: t(`${widgetData.image.caption}.string`),
                        }),
                      },
                    }
                  : {})}
                {...(widgetData.hint
                  ? {
                      hintDetails: {
                        label: t("Hint.string"),
                        open: false,
                        content: t(`${widgetData.hint}.string`),
                      },
                    }
                  : {})}
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Tab Comparison" ? (
              <TabComp
                key={props.descriptionID}
                preview={props.preview}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
                bp={props.bp}
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Video" ? (
              <Video
                key={props.descriptionID}
                preview={props.preview}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
                srt={
                  window.location.origin +
                  window.location.pathname +
                  `locales/${lang}/srt/${props.sceneData.Scrolly.descriptions[props.descriptionID - 1].props.srt}`
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Animation" ? (
              <Animation
                key={props.descriptionID}
                preview={props.preview}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Slider" ? (
              <Slider
                key={props.descriptionID}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "dataWidget" ? (
              <DataWidget
                key={props.descriptionID}
                data={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props
                }
                plotFunctions={props.plotFunctions}
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "PCR2" ? (
              <PCR2
                key={props.descriptionID}
                toNext={() => {
                  forceAnimationUpdate("click", "down");
                  props.forceScroll("nav");
                }}
                toPrevious={() => {
                  forceAnimationUpdate("click", "up");
                  props.forceScroll("nav");
                }}
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Histogram1" ? (
              <Histogram1
                key={props.descriptionID}
                i18next={i18next}
                intro={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.text
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Histogram2" ? (
              <Histogram2
                key={props.descriptionID}
                i18next={i18next}
                intro={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.text
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Histogram3" ? (
              <Histogram3
                key={props.descriptionID}
                i18next={i18next}
                intro={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.text
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "Histogram4" ? (
              <Histogram4
                key={props.descriptionID}
                i18next={i18next}
                intro={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.text
                }
              />
            ) : props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                .props.type === "NEQ" ? (
              <NEQ
                key={props.descriptionID}
                id={1}
                label={parse(
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.text
                )}
                feedback={parse(
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.feedback
                )}
                attempts={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.attempts
                }
                answer={
                  props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                    .props.answer
                }
              />
            ) : null}
          </div>
          <div
            className="widget-next-btn-container"
            style={{
              display:
                props.sceneData.Scrolly.descriptions[props.descriptionID - 1]
                  .props.type === "PCR2"
                  ? "none"
                  : null,
            }}
            id="widget-next-btn-container"
          >
            <button
              onMouseDown={(e) => e.preventDefault()}
              className="widget-nav-button"
              aria-label={t("next.string")}
              onClick={() => {
                forceAnimationUpdate("click", "down");
                props.forceScroll("nav");
              }}
            >
              <img
                src={ActiveArrow}
                alt=""
                role="presentation"
                className="widget-next-btn"
              />
            </button>
          </div>
        </div>
      </div>
    </FocusLock>
  );
}
